import { getAuthHeaders } from './authToken';

const API_BASE = process.env.REACT_APP_API_URL;

// Delegates to the shared helper so every call carries the signed-in user's Zitadel access token.
const getHeaders = (includeContentType = true) => getAuthHeaders(includeContentType);

/**
 * Get all slack events with pagination and filters
 * Returns SlackEvent objects with nested CustomerRequest (which may be null)
 * @returns {Promise<{totalCount: number, page: number, pageSize: number, items: SlackEventListItem[]}>}
 *
 * SlackEventListItem shape:
 * {
 *   id, eventType, channelId, eventTs, threadTs,
 *   slackUserId, slackUsername, isProcessed, processedAt, createdAt,
 *   customerRequest: {
 *     id, trackingNumber, slackEventTs, requestType, notes,
 *     customerName, customerPhone, customerEmail, portalUrl,
 *     status, agentSlackUserId, agentSlackUsername,
 *     resolvedBy, resolvedAt, createdAt, updatedAt,
 *     notifications: { sms, email },
 *     responses: [{ id, trackingNumber, responseData, respondedAt }]
 *   } | null
 * }
 */
export const getCustomerRequests = async ({
  page = 1,
  pageSize = 25,
  trackingNumber = '',
  status = '',
  channelId = '',
} = {}) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  });

  if (trackingNumber) queryParams.append('trackingNumber', trackingNumber);
  if (status) queryParams.append('status', status);
  if (channelId) queryParams.append('channelId', channelId);

  const response = await fetch(`${API_BASE}/api/customer-requests?${queryParams.toString()}`, {
    method: 'GET',
    headers: getHeaders(false),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch customer requests');
  }

  return response.json();
};

/**
 * Get a single slack event by ID with full details
 * Returns SlackEvent object with nested CustomerRequest (which may be null)
 * @param {number} id - SlackEvent ID
 * @returns {Promise<SlackEventDetail>}
 *
 * SlackEventDetail shape:
 * {
 *   id, eventType, channelId, eventTs, threadTs,
 *   slackUserId, slackUsername, isProcessed, processedAt, createdAt,
 *   customerRequest: {
 *     id, trackingNumber, slackEventTs, slackThreadTs, requestType, notes,
 *     customerName, customerPhone, customerEmail, portalUrl,
 *     status, agentSlackUserId, agentSlackUsername,
 *     resolvedBy, resolvedAt, createdAt, updatedAt,
 *     notifications: { sms, email },
 *     responses: [{ id, trackingNumber, responseData, respondedAt }]
 *   } | null
 * }
 */
export const getCustomerRequestById = async (id) => {
  const response = await fetch(`${API_BASE}/api/customer-requests/${id}`, {
    method: 'GET',
    headers: getHeaders(false),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch customer request');
  }

  return response.json();
};

/**
 * Reply to the Slack thread for a slack event
 * @param {number} id - SlackEvent ID
 * @param {string} message - Message to send
 */
export const replyToCustomerRequest = async (id, message) => {
  const response = await fetch(`${API_BASE}/api/customer-requests/${id}/reply`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ message }),
  });

  if (!response.ok) {
    throw new Error('Failed to send reply');
  }

  return response.json();
};

/**
 * Update customer request status
 * @param {number} id - SlackEvent ID (used to identify the associated CustomerRequest)
 * @param {number} status - Status enum: Pending=0, Notified=1, CustomerResponded=2, Resolved=3, Escalated=4
 * @param {string} resolvedBy - Optional name of person resolving the request
 */
export const updateCustomerRequestStatus = async (id, status, resolvedBy = null) => {
  const body = { status };
  if (resolvedBy) {
    body.resolvedBy = resolvedBy;
  }

  const response = await fetch(`${API_BASE}/api/customer-requests/${id}/status`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error('Failed to update status');
  }

  return response.json();
};

/**
 * Send a message to any Slack channel/thread
 */
export const sendSlackMessage = async ({ channel, text, threadTs = null, trackingNumber = null }) => {
  const body = { channel, text };
  if (threadTs) body.threadTs = threadTs;
  if (trackingNumber) body.trackingNumber = trackingNumber;

  const response = await fetch(`${API_BASE}/api/messages`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error('Failed to send message');
  }

  return response.json();
};

/**
 * Send a Slack thread reply by tracking number
 */
export const sendMessageByTracking = async (trackingNumber, text) => {
  const response = await fetch(`${API_BASE}/api/messages/by-tracking`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ trackingNumber, text }),
  });

  if (!response.ok) {
    throw new Error('Failed to send message');
  }

  return response.json();
};

/**
 * Get active slack channels for dropdown
 * @returns {Promise<Array<{id: number, channelId: string, channelName: string}>>}
 */
export const getSlackChannelsDropdown = async () => {
  const response = await fetch(`${API_BASE}/api/slack-channel-configurations/dropdown`, {
    method: 'GET',
    headers: getHeaders(false),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch slack channels');
  }

  return response.json();
};

// Status enum mapping
export const STATUS_ENUM = {
  Pending: 0,
  Notified: 1,
  CustomerResponded: 2,
  Resolved: 3,
  Escalated: 4,
};
